/**
 * Mobile Units – Mobile Portal
 *
 * Purpose: View units, equipment, drivers (profile cards); log activity. Same API as Ketchup Portal.
 * Location: mobile-portal/src/pages/MobileUnits.tsx
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { mobileUnitsAPI } from '@smartpay/api-client/ketchup';
import type { MobileUnit, MobileUnitDriver, MobileUnitEquipment, ActivityItem } from '@smartpay/api-client/ketchup';
import {
  Button,
  Card,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  useToast,
} from '@smartpay/ui';
import { Truck, Package, Users, Activity, User } from 'lucide-react';

const PAGE_SIZE = 10;
const NAMIBIAN_REGIONS = [
  'Khomas', 'Erongo', 'Oshana', 'Omusati', 'Ohangwena', 'Oshikoto',
  'Kavango East', 'Kavango West', 'Zambezi', 'Kunene', 'Otjozondjupa', 'Omaheke', 'Hardap', 'Karas',
];

type DetailTab = 'info' | 'equipment' | 'drivers' | 'activity';

export default function MobileUnits() {
  const [search, setSearch] = useState('');
  const [region, setRegion] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const [viewUnit, setViewUnit] = useState<MobileUnit | null>(null);
  const [detailTab, setDetailTab] = useState<DetailTab>('info');
  const [maintenanceForm, setMaintenanceForm] = useState({ type: 'inspection', description: '', partsUsed: '', cost: '', meterReading: '' });
  const [showMaintenanceForm, setShowMaintenanceForm] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: unitsResponse, isLoading } = useQuery({
    queryKey: ['mobile-units', region, status],
    queryFn: () => mobileUnitsAPI.getAll({ region: region || undefined, status: status || undefined }),
  });
  const { data: stats } = useQuery({
    queryKey: ['mobile-units-stats'],
    queryFn: () => mobileUnitsAPI.getStats(),
  });

  const unitId = viewUnit?.id ?? '';
  const { data: equipmentList = [] } = useQuery({
    queryKey: ['mobile-units-equipment', unitId],
    queryFn: () => mobileUnitsAPI.getEquipment(unitId),
    enabled: !!unitId,
  });
  const { data: driversList = [] } = useQuery({
    queryKey: ['mobile-units-drivers', unitId],
    queryFn: () => mobileUnitsAPI.getDrivers(unitId),
    enabled: !!unitId,
  });
  const { data: activityList = [] } = useQuery({
    queryKey: ['mobile-units-activity', unitId],
    queryFn: () => mobileUnitsAPI.getActivity(unitId),
    enabled: !!unitId,
  });

  const maintenanceMutation = useMutation({
    mutationFn: (body: { type: string; description: string; partsUsed?: string; cost?: number; meterReading?: string }) =>
      mobileUnitsAPI.postMaintenance(unitId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mobile-units-activity', unitId] });
      setShowMaintenanceForm(false);
      setMaintenanceForm({ type: 'inspection', description: '', partsUsed: '', cost: '', meterReading: '' });
    },
    onError: (e) => toast({ title: 'Record maintenance failed', description: e instanceof Error ? e.message : 'Please try again.', variant: 'destructive' }),
  });

  const raw = unitsResponse as { data?: MobileUnit[] } | undefined;
  const list = Array.isArray(raw) ? raw : (raw?.data ?? []);
  const filtered = list.filter(
    (u) =>
      (u.name?.toLowerCase().includes(search.toLowerCase()) ?? false) &&
      (region ? u.region === region : true) &&
      (status ? u.status === status : true)
  );
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE) || 1;
  const pageData = filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const formatCurrency = (n: number) =>
    new Intl.NumberFormat('en-NA', { style: 'currency', currency: 'NAD', minimumFractionDigits: 0 }).format(n);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Mobile Units</h1>
        <p className="text-muted-foreground">View units, equipment, drivers; log activity (inspection, repair, service)</p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="p-4">
            <p className="text-sm text-muted-foreground">Total units</p>
            <p className="text-2xl font-semibold">{stats.total}</p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-muted-foreground">Active</p>
            <p className="text-2xl font-semibold text-green-600">{stats.active}</p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-muted-foreground">Down</p>
            <p className="text-2xl font-semibold text-red-600">{stats.down ?? 0}</p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-muted-foreground">Volume today</p>
            <p className="text-2xl font-semibold">{formatCurrency(stats.totalVolumeToday ?? 0)}</p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-muted-foreground">Success rate</p>
            <p className="text-2xl font-semibold">{(stats.avgSuccessRate ?? 0).toFixed(1)}%</p>
          </Card>
        </div>
      )}

      <div className="flex flex-wrap justify-between items-center gap-4">
        <p className="text-muted-foreground">
          Total: <span className="font-semibold text-foreground">{filtered.length}</span> SmartPay Mobile
        </p>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Search by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-64"
          />
          <select
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="">All regions</option>
            {NAMIBIAN_REGIONS.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="down">Down</option>
            <option value="inactive">Inactive</option>
            <option value="low_liquidity">Low liquidity</option>
          </select>
        </div>
      </div>

      <Dialog
        open={!!viewUnit}
        onOpenChange={(open) => {
          if (!open) {
            setViewUnit(null);
            setDetailTab('info');
            setShowMaintenanceForm(false);
          }
        }}
      >
        <DialogContent aria-describedby="mobile-unit-details-desc" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>SmartPay Mobile unit details</DialogTitle>
            <DialogDescription id="mobile-unit-details-desc" className="sr-only">
              Unit info, Equipment, Drivers (profile cards), Activity.
            </DialogDescription>
          </DialogHeader>
          {viewUnit && (
            <div className="space-y-4 text-sm">
              <div className="flex gap-2 border-b pb-2">
                <Button variant={detailTab === 'info' ? 'default' : 'outline'} size="sm" onClick={() => setDetailTab('info')}>Info</Button>
                <Button variant={detailTab === 'equipment' ? 'default' : 'outline'} size="sm" onClick={() => setDetailTab('equipment')}><Package className="h-4 w-4 mr-1" /> Equipment</Button>
                <Button variant={detailTab === 'drivers' ? 'default' : 'outline'} size="sm" onClick={() => setDetailTab('drivers')}><Users className="h-4 w-4 mr-1" /> Drivers</Button>
                <Button variant={detailTab === 'activity' ? 'default' : 'outline'} size="sm" onClick={() => setDetailTab('activity')}><Activity className="h-4 w-4 mr-1" /> Activity</Button>
              </div>

              {detailTab === 'info' && (
                <div className="space-y-2">
                  <p><span className="font-medium text-muted-foreground">Name</span> {viewUnit.name}</p>
                  <p><span className="font-medium text-muted-foreground">Region</span> {viewUnit.region}</p>
                  <p><span className="font-medium text-muted-foreground">Status</span> {viewUnit.status}</p>
                  <p><span className="font-medium text-muted-foreground">Liquidity</span> {formatCurrency(viewUnit.liquidity ?? 0)}</p>
                  <p><span className="font-medium text-muted-foreground">Transactions today</span> {viewUnit.transactionsToday ?? 0}</p>
                  <p><span className="font-medium text-muted-foreground">Volume today</span> {formatCurrency(viewUnit.volumeToday ?? 0)}</p>
                </div>
              )}

              {detailTab === 'equipment' && (
                <div className="space-y-4">
                  <p className="font-medium">Equipment</p>
                  {equipmentList.length === 0 ? (
                    <p className="text-muted-foreground text-xs">No equipment recorded.</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Asset ID</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Issued</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(equipmentList as MobileUnitEquipment[]).map((eq) => (
                          <TableRow key={eq.id}>
                            <TableCell className="font-medium">{eq.assetId}</TableCell>
                            <TableCell>{eq.equipmentTypeCode}</TableCell>
                            <TableCell>{eq.status}</TableCell>
                            <TableCell>{eq.issuedAt ? new Date(eq.issuedAt).toLocaleDateString() : '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              )}

              {detailTab === 'drivers' && (
                <div className="space-y-4">
                  <p className="font-medium">Drivers (profile cards)</p>
                  {driversList.length === 0 ? (
                    <p className="text-muted-foreground text-xs">No drivers assigned.</p>
                  ) : (
                    <div className="grid gap-4 md:grid-cols-2">
                      {(driversList as MobileUnitDriver[]).map((d) => (
                        <Card key={d.id} className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                              <User className="h-6 w-6 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold">{d.name}</p>
                              <p className="text-xs text-muted-foreground">{d.role}</p>
                              {d.idNumber && <p className="text-xs">ID: {d.idNumber}</p>}
                              {d.phone && <p className="text-xs">Phone: {d.phone}</p>}
                              <span className={`inline-flex mt-1 rounded-full px-2 py-0.5 text-xs font-medium ${d.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-muted text-muted-foreground'}`}>{d.status}</span>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {detailTab === 'activity' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <p className="font-medium">Activity</p>
                    <Button size="sm" onClick={() => setShowMaintenanceForm(!showMaintenanceForm)}>
                      {showMaintenanceForm ? 'Cancel' : 'Log maintenance'}
                    </Button>
                  </div>
                  {showMaintenanceForm && (
                    <Card className="p-4 space-y-3">
                      <label className="block text-xs font-medium">Type</label>
                      <select value={maintenanceForm.type} onChange={(e) => setMaintenanceForm((f) => ({ ...f, type: e.target.value }))} className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                        <option value="inspection">Inspection</option>
                        <option value="repair">Repair</option>
                        <option value="service">Service</option>
                        <option value="other">Other</option>
                      </select>
                      <label className="block text-xs font-medium">Description *</label>
                      <Input placeholder="Description" value={maintenanceForm.description} onChange={(e) => setMaintenanceForm((f) => ({ ...f, description: e.target.value }))} className="text-sm" />
                      <label className="block text-xs font-medium">Parts used</label>
                      <Input placeholder="Parts used" value={maintenanceForm.partsUsed} onChange={(e) => setMaintenanceForm((f) => ({ ...f, partsUsed: e.target.value }))} className="text-sm" />
                      <label className="block text-xs font-medium">Cost (NAD)</label>
                      <Input type="number" placeholder="Cost" value={maintenanceForm.cost} onChange={(e) => setMaintenanceForm((f) => ({ ...f, cost: e.target.value }))} className="text-sm" />
                      <label className="block text-xs font-medium">Meter reading</label>
                      <Input placeholder="Meter reading" value={maintenanceForm.meterReading} onChange={(e) => setMaintenanceForm((f) => ({ ...f, meterReading: e.target.value }))} className="text-sm" />
                      <Button
                        size="sm"
                        disabled={!maintenanceForm.description.trim() || maintenanceMutation.isPending}
                        onClick={() => maintenanceMutation.mutate({ type: maintenanceForm.type, description: maintenanceForm.description.trim(), partsUsed: maintenanceForm.partsUsed || undefined, cost: maintenanceForm.cost ? Number(maintenanceForm.cost) : undefined, meterReading: maintenanceForm.meterReading || undefined })}
                      >
                        {maintenanceMutation.isPending ? 'Logging…' : 'Log maintenance'}
                      </Button>
                    </Card>
                  )}
                  {activityList.length === 0 ? (
                    <p className="text-muted-foreground text-xs">No activity yet.</p>
                  ) : (
                    <ul className="space-y-2 text-sm">
                      {(activityList as ActivityItem[]).map((a) => (
                        <li key={a.id} className="flex justify-between border-b pb-2">
                          <span><strong>{a.type}</strong> {a.description ?? a.maintenanceType ?? a.assetId ?? ''} {a.cost != null ? ` · ${formatCurrency(a.cost)}` : ''}</span>
                          <span className="text-muted-foreground">{a.at ? new Date(a.at).toLocaleString() : ''}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setViewUnit(null)}>Close</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Card>
        <div className="p-0">
          {isLoading ? (
            <div className="p-6 text-center text-muted-foreground">Loading mobile units...</div>
          ) : pageData.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground">No mobile units found.</div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Region</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Liquidity</TableHead>
                    <TableHead>Transactions today</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageData.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell>{u.region}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${u.status === 'active' ? 'bg-green-100 text-green-800' : u.status === 'down' ? 'bg-red-100 text-red-800' : u.status === 'low_liquidity' ? 'bg-amber-100 text-amber-800' : 'bg-muted text-muted-foreground'}`}>{u.status}</span>
                      </TableCell>
                      <TableCell>{formatCurrency(u.liquidity ?? 0)}</TableCell>
                      <TableCell>{u.transactionsToday ?? 0}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" onClick={() => setViewUnit(u)}>View</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t px-4 py-3">
                  <p className="text-sm text-muted-foreground">Page {page + 1} of {totalPages} ({filtered.length} results)</p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                    <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>Next</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
